/* eslint-disable max-len */
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Document } from '@1po/1po-bff-fe-spec/generated/catalog/references/iam/model/Document';
import { CatalogSource } from '@1po/1po-bff-fe-spec/generated/common/vehicle/CatalogSource';
import { ROUTER_CART, ROUTER_ESTIMATE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { ReactComponent as RenativeLogo } from 'assets/CarBrand/brand_renative_mini.svg';
import { CompareIcon, TimesCircleIcon } from 'assets/icons';
import { AddToEstimateButtonAndDialog } from 'components/AddToEstimate/AddToEstimateButtonAndDialog';
import BestSeller from 'components/BestSeller';
import { CopyReferenceWithFeedback, CopySelectionWrapper } from 'components/CopyReferenceFeedback';
import { ErrorWithLabel } from 'components/DataContainer';
import { DiscountCorner } from 'components/Discount';
import { DocumentationAlertButton } from 'components/DocumentationAlertButton';
import { DocumentScrollModal } from 'components/DocumentScrollModal/DocumentScrollModal';
import ProductModal from 'components/ProductModal';
import { ReferencePriceSection } from 'components/ReferencePriceSection';
import StockDisplay from 'components/StockInfo';
import { addReferenceByRefNumberRequest, addReferenceUnderVehicleRequest } from 'domains/basket/Basket.store';
import { DATAHUB, IAM, STANDARD } from 'domains/catalog/Catalog.types';
import { convertReferenceToEstimateReference } from 'domains/estimate/Estimate.mapper';
import { addCatalogReference } from 'domains/estimate/Estimate.store';
import { getIsStockAvailable, getPrice, getStockInfo } from 'domains/references';
import { GarageView, SparePartsViewType } from 'domains/user';
import { SReferenceImageCard } from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/IAMReferenceCard.styled';
import ReferenceImage from 'pages/CatalogPage/IAM/SubcategorySection/SparePartsSection/ReferenceCardsContainer/ReferenceCard/ReferenceImage';
import { theme } from 'styles';
import {
  Box,
  capitalize,
  CenterFlex,
  Flex,
  Icon,
  Image,
  Label,
  MarginBox,
  NotificationLink,
  notifyTop,
  Text,
  URL,
} from 'UI';
import { AddToEstimatePositionWrapper, ProductPreviewCard } from 'UI/ProductPreview/ProductPreview.styled';
import { getData, hasData, isError } from 'utils';
import {
  TRACKING_EVENT_ADD_TO_ESTIMATE,
  TRACKING_EVENT_GO_TO_CART_SHORTCUT,
  TRACKING_EVENT_GO_TO_ESTIMATE,
} from 'utils/eventTracker/EventTracker.types';
import { convertUniversalProductToUniversalProductReference } from '../../domains/catalog/Catalog.mapper';
import {
  getLastSearchedVehicleKey,
  getSearchVehicleResult,
  getUniversalProduct,
} from '../../domains/catalog/Catalog.store';

export interface ProductPreviewProps {
  imageUrl: string | undefined;
  productName: string | undefined;
  referenceNumber: string | undefined;
  sparePartsView: SparePartsViewType;
  catalogSource?: CatalogSource;
  renative?: boolean;
  isApplicableToCurrentVehicle?: boolean;
  isUniversalProduct?: boolean;
  showCompare?: boolean;
  handleClickCompare?: (reference: string) => void;
  brandImage?: string;
  vehicleKey?: string;
  documents?: Document[];
}

const AvailableProductImage = ({
  isIamProduct,
  brandImage,
  imageUrl,
  documents,
}: {
  isIamProduct: boolean;
  brandImage: string | undefined;
  imageUrl: string | undefined;
  documents?: Document[];
}) => {
  const [documentsModalVisible, setDocumentsModalVisible] = useState(false);
  if (isIamProduct) {
    return (
      <DocumentScrollModal
        documents={documents ?? []}
        visible={documentsModalVisible}
        onVisibleChange={setDocumentsModalVisible}
      >
        <SReferenceImageCard>
          <ReferenceImage brandImage={brandImage} referenceImage={imageUrl} imageHeight={250} imageWidth={250} />
        </SReferenceImageCard>
      </DocumentScrollModal>
    );
  }
  return (
    <CenterFlex>
      <Image
        src={imageUrl}
        type={URL}
        alt={'Universal product'}
        maxWidth={250}
        maxHeight={250}
        fallbackComponent={<Image src={'placeholder.png'} alt={'Universal product'} width={250} height={250} />}
      />
    </CenterFlex>
  );
};

const UnavailableProductImage = () => {
  return (
    <CenterFlex direction="column" background={theme.color.shadow_7}>
      <Icon IconComponent={TimesCircleIcon} color={theme.color.palette_red_7} width={45} height={45} />
      <MarginBox mb={15} />
      <Box width={162}>
        <Text type="base" color="grey25">
          <Trans i18nKey="catalog.reference.unavailable">Looks like this item is not available in your country.</Trans>
        </Text>
      </Box>
    </CenterFlex>
  );
};

const ReferenceNumberSection = ({
  isIamProduct,
  availableInUserCountry,
  referenceNumber,
  renative,
  isUniversalProduct,
}: {
  isIamProduct: boolean;
  availableInUserCountry: boolean;
  referenceNumber: string;
  isUniversalProduct: boolean;
  renative?: boolean;
}) => {
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const vehicle = useSelector((state: RootState) => getSearchVehicleResult(state, vehicleKey));
  const product = useSelector((state: RootState) => getUniversalProduct(state, referenceNumber));
  const vehicleDetail = vehicle?.vehicleDetail;
  const productReference = convertUniversalProductToUniversalProductReference(getData(product));

  return (
    <Flex direction={'row'} align={'center'}>
      <Box height={22}>
        <CopySelectionWrapper referenceNumber={referenceNumber}>
          {isIamProduct ? (
            <Text type="base" color={availableInUserCountry ? undefined : 'grey55'}>
              <Trans i18nKey={'catalog.reference.referenceNumber.short'} tOptions={{ referenceNumber }}>
                Ref: {{ referenceNumber }}
              </Trans>
            </Text>
          ) : (
            <ProductModal
              triggerComponent={
                <Text
                  type="base"
                  color={availableInUserCountry ? undefined : 'grey55'}
                  hoverUnderLine
                  cursor={'pointer'}
                >
                  <Trans i18nKey={'catalog.reference.referenceNumber.short'} tOptions={{ referenceNumber }}>
                    Ref: {{ referenceNumber }}
                  </Trans>
                </Text>
              }
              referenceNumber={referenceNumber}
              isUniversalProduct={isUniversalProduct}
            />
          )}
        </CopySelectionWrapper>
      </Box>
      <Flex align={'baseline'}>
        <MarginBox mr={10} />
        <CopyReferenceWithFeedback referenceNumber={referenceNumber} />
        {productReference?.brand === 'MOTRIO' && (
          <DocumentationAlertButton
            reference={productReference}
            vehicleDetail={vehicleDetail}
            vehicleRegistrationNumber={vehicleDetail?.vrn ?? vehicleDetail?.vehicleKey}
          />
        )}
        {isUniversalProduct && renative && (
          <Flex alignSelf={'center'}>
            <MarginBox mr={10} />
            <RenativeLogo />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

const ReferenceNameSection = ({
  isIamProduct,
  productName,
  availableInUserCountry,
  referenceNumber,
  isUniversalProduct,
}: {
  isIamProduct: boolean;
  productName: string | undefined;
  availableInUserCountry: boolean;
  referenceNumber: string;
  isUniversalProduct: boolean;
}) => {
  return (
    <MarginBox mt={16}>
      <Box height={22}>
        <Label width={210}>
          {isIamProduct ? (
            <Text type="base" bold color={availableInUserCountry ? undefined : 'grey55'} ellipsis>
              {productName ?? ''}
            </Text>
          ) : (
            <ProductModal
              triggerComponent={
                <Text
                  type="base"
                  bold
                  color={availableInUserCountry ? undefined : 'grey55'}
                  ellipsis
                  hoverUnderLine
                  cursor={'pointer'}
                >
                  {capitalize(productName?.toLowerCase() ?? '')}
                </Text>
              }
              referenceNumber={referenceNumber}
              isUniversalProduct={isUniversalProduct}
            />
          )}
        </Label>
      </Box>
    </MarginBox>
  );
};

export const ProductPreview = ({
  imageUrl,
  productName,
  referenceNumber,
  sparePartsView,
  catalogSource,
  renative,
  isApplicableToCurrentVehicle,
  isUniversalProduct,
  showCompare,
  handleClickCompare,
  brandImage,
  vehicleKey,
  documents,
}: ProductPreviewProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const isStockAvaliable = useSelector((state: RootState) => getIsStockAvailable(state, referenceNumber));
  const availableInUserCountry = isUniversalProduct ? isStockAvaliable : true;
  const stockInfo = useSelector((state: RootState) => getStockInfo(state, referenceNumber));
  const price = useSelector((state: RootState) => getPrice(state, referenceNumber));
  const isIamProduct = catalogSource === IAM;

  if (!referenceNumber) {
    return null;
  }

  function handleIAMAddToCartClick() {
    if (!referenceNumber) {
      return;
    }
    notifyTop(
      'success',
      <Trans i18nKey={'catalog.reference_card.added_to_basket.description'}>
        {'Reference has been added to your cart'}
      </Trans>,
      undefined,
      <NotificationLink
        onClick={() => {
          trackAppEvent(TRACKING_EVENT_GO_TO_CART_SHORTCUT);
          history.push(ROUTER_CART);
        }}
      >
        <Trans i18nKey={'catalog.reference_card.added_to_basket.go_to_cart'}>{'Go to cart'}</Trans>
      </NotificationLink>,
    );

    if (vehicleKey) {
      dispatch(
        addReferenceUnderVehicleRequest({
          referenceToAdd: { referenceNumber, sellerNumber: undefined },
          catalogSource: IAM,
        }),
      );
      return;
    }
    dispatch(
      addReferenceByRefNumberRequest({
        reference: referenceNumber,
      }),
    );
  }

  function handleUniversalProductAddToCartClick() {
    if (!referenceNumber) {
      return;
    }
    notifyTop(
      'success',
      <Trans i18nKey={'catalog.universal_products.reference_card.added_to_basket.description'}>
        {'Universal product has been added to your cart'}
      </Trans>,
      undefined,
      <NotificationLink
        onClick={() => {
          trackAppEvent(TRACKING_EVENT_GO_TO_CART_SHORTCUT);
          history.push(ROUTER_CART);
        }}
      >
        <Trans i18nKey={'catalog.reference_card.added_to_basket.go_to_cart'}>{'Go to cart'}</Trans>
      </NotificationLink>,
    );
    dispatch(
      addReferenceByRefNumberRequest({
        reference: referenceNumber,
      }),
    );
  }

  const handleAddToCartClick = () => {
    if (!referenceNumber) {
      return;
    }
    if (isIamProduct) {
      handleIAMAddToCartClick();
    } else {
      handleUniversalProductAddToCartClick();
    }
  };

  const handleAddToEstimateClick = (quantity?: number) => {
    if (hasData(price)) {
      notifyTop(
        'success',
        <Trans count={quantity ?? 1} i18nKey={'catalog.reference_card.added_to_estimate.description.v2'}>
          {'{{ count }} Reference has been added to your estimate.'}
        </Trans>,
        undefined,
        <NotificationLink
          onClick={() => {
            trackAppEvent(TRACKING_EVENT_GO_TO_ESTIMATE);

            history.push(ROUTER_ESTIMATE);
          }}
        >
          <Trans i18nKey={'catalog.reference_card.added_to_estimate.go_to_estimate'}>{'Go to estimate'}</Trans>
        </NotificationLink>,
      );
      trackAppEvent(TRACKING_EVENT_ADD_TO_ESTIMATE);
      dispatch(
        addCatalogReference({
          reference: convertReferenceToEstimateReference(
            referenceNumber,
            productName ?? '',
            price,
            true,
            'DATAHUB',
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            quantity,
          ),
        }),
      );
    }
  };

  return (
    <ProductPreviewCard direction={'column'} minWidth={300} maxWidth={300} minHeight={500} align={'center'}>
      {sparePartsView === GarageView && <DiscountCorner reference={referenceNumber} type={'catalog'} />}
      {getData(price)?.garageView?.vatExcludedPrice && (
        <AddToEstimatePositionWrapper>
          <AddToEstimateButtonAndDialog handleAddToEstimateClick={handleAddToEstimateClick} size={'lg'} />
        </AddToEstimatePositionWrapper>
      )}
      <Flex direction={'column'}>
        <Box width={250} height={250}>
          {availableInUserCountry ? (
            <AvailableProductImage
              documents={documents}
              isIamProduct={isIamProduct}
              brandImage={brandImage}
              imageUrl={imageUrl}
            />
          ) : (
            <UnavailableProductImage />
          )}
        </Box>
        <BestSeller isBestseller={false} />
        <ReferenceNameSection
          isIamProduct={isIamProduct}
          availableInUserCountry={availableInUserCountry}
          productName={productName}
          referenceNumber={referenceNumber}
          isUniversalProduct={!!isUniversalProduct}
        />
        <ReferenceNumberSection
          isIamProduct={isIamProduct}
          availableInUserCountry={availableInUserCountry}
          referenceNumber={referenceNumber}
          renative={renative}
          isUniversalProduct={!!isUniversalProduct}
        />
        <Flex direction={'column'} maxWidth={250}>
          {isError(stockInfo) && isError(price) ? (
            <ErrorWithLabel
              label={t(
                'common.price_and_stock_backend_error',
                'Price and stocks are temporarily unavailable, please try again later.',
              )}
              narrow
            />
          ) : (
            <>
              <StockDisplay
                referenceNumber={referenceNumber}
                isApplicableToCurrentVehicle={isApplicableToCurrentVehicle}
                vehicleKey={undefined}
                narrow
              />
              <Flex>
                <ReferencePriceSection
                  align={'left'}
                  referenceNumber={referenceNumber}
                  handleAddToCartClick={handleAddToCartClick}
                  vehicleKey={vehicleKey}
                  sparePartsView={sparePartsView}
                  catalogSource={catalogSource ?? DATAHUB}
                  referenceType={STANDARD}
                  narrow
                  withIcon
                  buttonSize={'large'}
                  buttonShape={'round'}
                  externalButton={
                    showCompare && (
                      <Icon
                        IconComponent={CompareIcon}
                        height={42}
                        width={48}
                        onClick={() => {
                          handleClickCompare?.(referenceNumber);
                        }}
                      />
                    )
                  }
                />
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </ProductPreviewCard>
  );
};
